import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";import { api } from "@/lib/api";
import { ScorePill, StatusBadge } from "@/components/StatusBadge";
import { FASE_LABELS, PLANO_LABELS, CLIMA_LABELS, RESPONSAVEL_LABELS, STATUS_LABELS, brl, monthLabel, fmtPct, statusColor, toCurrentMonthRef, TREINO_MODULOS } from "@/lib/format";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { AlertTriangle, CheckCircle2, Circle, Clock, AlertCircle, ArrowLeft, Plus, X, Send, Pencil, MinusCircle, Trash2, CheckSquare, Coffee, GraduationCap } from "lucide-react";
import HistoricoTab from "@/components/HistoricoTab";
import IssuesTab from "@/components/IssuesTab";
import { ModulosCheckboxes, CompleteMeetingTaskModal, CompleteTreinamentoTaskModal } from "@/components/ActivityModals";

const TABS = [
    { id: "visao", label: "Visão Geral" },
    { id: "uso", label: "Indicadores de Uso" },
    { id: "jornada", label: "Jornada e Checklist" },
    { id: "reunioes", label: "Reuniões e Clima" },
    { id: "acoes", label: "Plano de Ação" },
    { id: "historico", label: "Histórico" },
    { id: "issues", label: "Issues/Tasks" },
];

const ESFERA_LABEL = { federal: "Federal", estadual: "Estadual", municipal: "Municipal" };

export default function ContractDetail() {
    const { id } = useParams();
    const [tab, setTab] = useState("visao");
    const [contract, setContract] = useState(null);
    const [usage, setUsage] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [actions, setActions] = useState([]);
    const [scoreHistory, setScoreHistory] = useState([]);

    async function refresh() {
        const [c, u, m, t, a, sh] = await Promise.all([
            api.get(`/contracts/${id}`),
            api.get(`/contracts/${id}/usage`),
            api.get(`/contracts/${id}/meetings`),
            api.get(`/contracts/${id}/tasks`),
            api.get(`/contracts/${id}/actions`),
            api.get(`/contracts/${id}/score-history`),
        ]);
        setContract(c.data); setUsage(u.data); setMeetings(m.data);
        setTasks(t.data); setActions(a.data); setScoreHistory(sh.data);
    }

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

    if (!contract) return <div className="text-sm text-slate-500">Carregando contrato…</div>;

    const isPersonalizado = contract.plano === "personalizado" || contract.plano === "especial";
    const personalizacaoMissing = isPersonalizado && !contract.data_inicio_personalizacao;

    return (
        <div className="space-y-6 fade-in" data-testid={`contract-detail-${id}`}>
            <div>
                <Link to="/contratos" className="text-xs text-slate-500 hover:text-[#0F2044] inline-flex items-center gap-1" data-testid="back-to-contracts">
                    <ArrowLeft className="h-3 w-3" /> Contratos
                </Link>
                <div className="mt-2 flex items-start justify-between gap-6">
                    <div>
                        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-medium">{PLANO_LABELS[contract.plano]} · {FASE_LABELS[contract.fase_atual]}</div>
                        <h1 className="font-display font-bold text-3xl text-[#0F2044] mt-1">{contract.organizacao_nome}</h1>
                        <div className="text-sm text-slate-500 mt-1">Esfera {ESFERA_LABEL[contract.esfera] || contract.esfera || "—"}</div>
                    </div>
                    <div className="flex items-start gap-3">
                        <Link to={`/relatorios/executivo/${contract.id}`} data-testid="open-executive-report"
                            className="text-xs font-semibold border border-[#0F2044]/30 text-[#0F2044] hover:bg-[#0F2044]/5 px-3 py-2 rounded-md inline-flex items-center gap-1.5 self-start mt-3">
                            Relatório Executivo
                        </Link>
                        <div className="bg-white border border-slate-200 rounded-lg p-5 min-w-[260px]">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Score atual</div>
                            <div className="mt-2"><ScorePill score={contract.score} /></div>
                            <div className="text-xs text-slate-500 mt-2">
                                Uso {contract.pontuacao_uso} + Clima {contract.pontuacao_clima} ({CLIMA_LABELS[contract.clima_recente] || "sem clima"})
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {personalizacaoMissing && (
                <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 flex items-center gap-3" data-testid="alert-personalizacao-vazia">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    <div className="flex-1 text-sm">
                        <span className="font-semibold text-amber-800">Atenção: data de início da personalização não preenchida.</span>
                        <span className="text-amber-700"> As tarefas com prazo DP ficam sem data prevista, mas as demais ações da jornada podem ser cumpridas normalmente antes do preenchimento.</span>
                    </div>
                    <button
                        onClick={() => setTab("visao")}
                        className="text-xs font-semibold text-amber-800 hover:underline"
                        data-testid="alert-go-to-personalizacao"
                    >
                        Preencher
                    </button>
                </div>
            )}

            <nav className="border-b border-slate-200 flex gap-1" data-testid="contract-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        data-testid={`tab-${t.id}`}
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "border-[#0F2044] text-[#0F2044]" : "border-transparent text-slate-500 hover:text-[#0F2044]"}`}
                    >
                        {t.label}
                    </button>
                ))}
            </nav>

            {tab === "visao" && <VisaoGeralTab contract={contract} tasks={tasks} scoreHistory={scoreHistory} onUpdate={refresh} />}
            {tab === "uso" && <UsoTab contract={contract} usage={usage} onUpdate={refresh} />}
            {tab === "jornada" && <JornadaTab contract={contract} tasks={tasks} onUpdate={refresh} />}
            {tab === "reunioes" && <ReunioesTab contract={contract} meetings={meetings} onUpdate={refresh} />}
            {tab === "acoes" && <AcoesTab contract={contract} actions={actions} onUpdate={refresh} />}
            {tab === "historico" && <HistoricoTab contract={contract} />}
            {tab === "issues" && <IssuesTab contract={contract} />}
        </div>
    );
}

// -------------- VISÃO GERAL ------------------------------------------
function VisaoGeralTab({ contract, tasks, scoreHistory, onUpdate }) {
    const next3 = useMemo(() => tasks.filter(t => t.computed_status !== "concluido" && t.computed_status !== "nao_se_aplica" && t.predicted_date).sort((a, b) => a.predicted_date.localeCompare(b.predicted_date)).slice(0, 3), [tasks]);
    const phases = (contract.plano === "personalizado" || contract.plano === "especial")
        ? ["onboarding", "personalizacao", "adocao", "expansao", "renovacao"]
        : ["onboarding", "adocao", "expansao", "renovacao"];
    const phaseIdx = phases.indexOf(contract.fase_atual);
    const [dp, setDp] = useState(contract.data_inicio_personalizacao || "");
    const [saving, setSaving] = useState(false);
    const [savingEntregue, setSavingEntregue] = useState(false);  // M4
    const [editOpen, setEditOpen] = useState(false);  // #10 modal edição cadastrais
    const [checkingId, setCheckingId] = useState(null);  // #3 loading do check inline
    const [entregaOpen, setEntregaOpen] = useState(false);  // M8 — modal de data real de entrega
    const [savingFase, setSavingFase] = useState(false);   // M15 — fase manual

    // M8 — confirmar entrega da personalização com a data real informada
    async function confirmEntrega(dataEntrega) {
        setSavingEntregue(true);
        try {
            await api.patch(`/contracts/${contract.id}`, { personalizacao_entregue_em: dataEntrega, data_entrega_produto: dataEntrega });
            setEntregaOpen(false);
            await onUpdate();
        } finally { setSavingEntregue(false); }
    }
    async function desfazerEntrega() {
        setSavingEntregue(true);
        try {
            await api.patch(`/contracts/${contract.id}`, { personalizacao_entregue_em: "" });
            await onUpdate();
        } finally { setSavingEntregue(false); }
    }

    // M15 — ajustar a fase manualmente (ou voltar ao automático com valor vazio)
    async function setFaseManual(val) {
        setSavingFase(true);
        try {
            await api.patch(`/contracts/${contract.id}`, { fase_manual: val });
            await onUpdate();
        } finally { setSavingFase(false); }
    }

    // M4 — marcar/desfazer personalização entregue
    async function toggleEntregue() {
        if (contract.personalizacao_entregue_em) { await desfazerEntrega(); }
        else { setEntregaOpen(true); }
    }

    // M1 (iter.9) — editar a data da entrega já registrada
    async function updateEntregueDate(val) {
        setSavingEntregue(true);
        try {
            await api.patch(`/contracts/${contract.id}`, { personalizacao_entregue_em: val });
            await onUpdate();
        } finally { setSavingEntregue(false); }
    }

    async function quickComplete(t) {
        setCheckingId(t.id);
        try {
            await api.patch(`/tasks/${t.id}`, { status: "concluido", observacao: "Concluída pela Visão Geral" });
            await onUpdate();
        } finally { setCheckingId(null); }
    }

    async function saveDp() {
        if (!dp) return;
        setSaving(true);
        try {
            await api.patch(`/contracts/${contract.id}`, { data_inicio_personalizacao: dp });
            await onUpdate();
        } finally { setSaving(false); }
    }

    return (
        <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 space-y-6">
                <Card
                    title="Dados cadastrais"
                    testid="card-dados-cadastrais"
                    headerAction={
                        <button
                            onClick={() => setEditOpen(true)}
                            data-testid="edit-cadastrais-button"
                            className="text-xs font-semibold border border-slate-300 hover:border-[#0F2044] text-[#0F2044] px-3 py-1.5 rounded-md inline-flex items-center gap-1"
                        >
                            <Pencil className="h-3 w-3" /> Editar
                        </button>
                    }
                >
                    <DL>
                        <DT label="Organização">{contract.organizacao_nome}</DT>
                        <DT label="Esfera">{ESFERA_LABEL[contract.esfera] || contract.esfera || "—"}</DT>
                        <DT label="Plano">{PLANO_LABELS[contract.plano]} · {contract.creditos_mensais} créditos/mês</DT>
                        <DT label="Assinatura">{brl(contract.data_assinatura)}</DT>
                        <DT label="Vencimento">{brl(contract.data_vencimento)}</DT>
                        <DT label="Entrega do produto">{brl(contract.data_entrega_produto)}</DT>
                        <DT label="Vendedor responsável">{contract.vendedor_responsavel || contract.cs_responsavel || "—"}</DT>
                        <DT label="Ponto focal">{contract.ponto_focal?.nome || "—"} <span className="text-slate-400">·</span> {contract.ponto_focal?.cargo || "—"}</DT>
                        <DT label="Contato PF">{contract.ponto_focal?.email || "—"} <span className="text-slate-400">·</span> {contract.ponto_focal?.telefone || "—"}</DT>
                        <DT label="Fiscal">{contract.ponto_focal?.fiscal_nome || "—"}</DT>
                        <DT label="Contato fiscal">{contract.ponto_focal?.fiscal_email || "—"} <span className="text-slate-400">·</span> {contract.ponto_focal?.fiscal_telefone || "—"}</DT>
                    </DL>
                </Card>

                {editOpen && (
                    <EditContractModal
                        contract={contract}
                        onClose={() => setEditOpen(false)}
                        onSaved={async () => { setEditOpen(false); await onUpdate(); }}
                    />
                )}

                {entregaOpen && (
                    <EntregaModal
                        contract={contract}
                        saving={savingEntregue}
                        onClose={() => setEntregaOpen(false)}
                        onConfirm={confirmEntrega}
                    />
                )}

                {(contract.plano === "personalizado" || contract.plano === "especial") && (
                    <Card title="Data de início da personalização (DP Dia 1)" testid="card-dp">
                        <div className="flex items-end gap-3">
                            <div className="flex-1">
                                <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Data DP1</label>
                                <input type="date" value={dp || ""} onChange={(e) => setDp(e.target.value)}
                                    data-testid="dp-input"
                                    className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#0F2044] focus:ring-1 focus:ring-[#0F2044]/30 focus:outline-none" />
                            </div>
                            {contract.prazo_entrega_personalizado && (
                                <div className="flex-1 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="dp-prazo-final">
                                    <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-700 font-semibold">Prazo final da entrega do personalizado</div>
                                    <div className="text-sm font-mono-tab font-semibold text-emerald-800 mt-0.5">{brl(contract.prazo_entrega_personalizado)}</div>
                                    <div className="text-[10px] text-emerald-700/80">DP1 + 45 dias úteis (seg–sex)</div>
                                </div>
                            )}
                            <button onClick={saveDp} disabled={saving || !dp}
                                data-testid="dp-save-button"
                                className="bg-[#0F2044] hover:bg-[#172E5E] text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60">
                                {saving ? "Salvando…" : "Salvar"}
                            </button>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">Após preencher, os prazos das tarefas DP e o prazo final de entrega são calculados automaticamente.</p>

                        {/* M4 — check de personalização entregue */}
                        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3" data-testid="personalizacao-entregue-row">
                            {contract.personalizacao_entregue_em ? (
                                <>
                                    <div className="flex items-center gap-2 text-sm flex-wrap">
                                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                        <span className="font-semibold text-emerald-700">Personalização entregue</span>
                                        <span className="text-slate-500">em</span>
                                        {/* M1 — data da entrega editável */}
                                        <input type="date" value={contract.personalizacao_entregue_em}
                                            onChange={(e) => e.target.value && updateEntregueDate(e.target.value)}
                                            data-testid="personalizacao-entregue-date-input"
                                            className="px-2 py-1 rounded-md border border-emerald-300 text-sm text-emerald-800 bg-emerald-50/50 focus:border-emerald-500 focus:outline-none" />
                                    </div>
                                    <button onClick={toggleEntregue} disabled={savingEntregue} data-testid="personalizacao-desfazer-button"
                                        className="text-xs text-slate-500 hover:text-red-600 underline disabled:opacity-50">
                                        Desfazer
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="text-sm text-slate-600">A personalização já foi entregue ao cliente?</div>
                                    <button onClick={toggleEntregue} disabled={savingEntregue} data-testid="personalizacao-entregue-button"
                                        className="text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 disabled:opacity-60">
                                        <CheckCircle2 className="h-3.5 w-3.5" /> {savingEntregue ? "Salvando…" : "Marcar como entregue"}
                                    </button>
                                </>
                            )}
                        </div>
                    </Card>
                )}

                <Card title="Histórico de score (últimos 6 meses)" testid="card-score-history">
                    {scoreHistory.length === 0 ? (
                        <div className="text-sm text-slate-500 py-3">Sem histórico de score ainda.</div>
                    ) : (
                        <div className="h-56">
                            <ResponsiveContainer>
                                <LineChart data={scoreHistory.map(h => ({ ...h, label: monthLabel(h.mes_ref) }))}>
                                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="score" stroke="#0F2044" strokeWidth={2} dot={{ fill: "#0F2044" }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </Card>
            </div>

            <div className="space-y-6">
                <Card title="Timeline da jornada" testid="card-timeline">
                    <ul className="space-y-2">
                        {phases.map((p, i) => (
                            <li key={p} className="flex items-center gap-3" data-testid={`timeline-${p}`}>
                                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                                    i < phaseIdx ? "bg-emerald-100 text-emerald-700" :
                                    i === phaseIdx ? "bg-[#0F2044] text-white" :
                                    "bg-slate-100 text-slate-400"
                                }`}>{i + 1}</div>
                                <div className="flex-1">
                                    <div className={`text-sm font-medium ${i === phaseIdx ? "text-[#0F2044]" : "text-slate-600"}`}>
                                        {FASE_LABELS[p]}
                                    </div>
                                </div>
                                {i === phaseIdx && <StatusBadge status={contract.status} />}
                            </li>
                        ))}
                    </ul>
                    {/* M15 — edição manual da etapa atual */}
                    <div className="mt-4 pt-4 border-t border-slate-100" data-testid="fase-manual-control">
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">
                            Etapa atual {contract.fase_manual
                                ? <span className="ml-1 badge-status" style={{ background: "#FEF3C7", color: "#92400E" }}>manual</span>
                                : <span className="ml-1 text-slate-400 normal-case tracking-normal">(automática)</span>}
                        </label>
                        <div className="flex items-center gap-2">
                            <select
                                value={contract.fase_manual || ""}
                                disabled={savingFase}
                                onChange={(e) => setFaseManual(e.target.value)}
                                data-testid="fase-manual-select"
                                className="flex-1 px-3 py-2 rounded-md border border-slate-300 text-sm bg-white focus:border-[#0F2044] focus:outline-none disabled:opacity-60">
                                <option value="">Automático (por tempo de contrato)</option>
                                {phases.map(p => <option key={p} value={p}>{FASE_LABELS[p]}</option>)}
                            </select>
                            {contract.fase_manual && (
                                <button onClick={() => setFaseManual("")} disabled={savingFase} data-testid="fase-manual-reset"
                                    className="text-xs font-semibold text-slate-500 hover:text-[#0F2044] underline whitespace-nowrap disabled:opacity-60">
                                    Voltar ao automático
                                </button>
                            )}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">Ajuste manual sobrescreve a etapa calculada automaticamente. Não altera o score.</p>
                    </div>
                </Card>

                <Card title="Próximas 3 tarefas" testid="card-next-tasks">
                    {next3.length === 0 ? (
                        <div className="text-sm text-slate-500">Sem tarefas pendentes.</div>
                    ) : (
                        <ul className="space-y-3">
                            {next3.map((t) => (
                                <li key={t.id} className="text-sm" data-testid={`next-task-${t.id}`}>
                                    <div className="flex items-start gap-2">
                                        <button
                                            onClick={() => quickComplete(t)}
                                            disabled={checkingId === t.id}
                                            data-testid={`next-task-check-${t.id}`}
                                            title="Marcar como concluída"
                                            className="mt-0.5 disabled:opacity-50 hover:scale-110 transition-transform"
                                        >
                                            <TaskStatusIcon status={t.computed_status} />
                                        </button>
                                        <span className="font-medium text-[#0F2044]">{t.descricao}</span>
                                    </div>
                                    <div className="text-xs text-slate-500 ml-6">
                                        {t.responsavel} · prazo {brl(t.predicted_date)}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>
        </div>
    );
}

// M8 — modal para informar a data real de entrega do personalizado
function EntregaModal({ contract, saving, onClose, onConfirm }) {
    const [data, setData] = useState(contract.data_entrega_produto || contract.personalizacao_entregue_em || new Date().toISOString().slice(0, 10));
    const [error, setError] = useState("");
    function submit() {
        if (!data) { setError("Informe a data real da entrega."); return; }
        onConfirm(data);
    }
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="entrega-modal" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 fade-in" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-600 font-semibold">Entrega do personalizado</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">Marcar como entregue</h3>
                    </div>
                    <button onClick={onClose} data-testid="entrega-close" className="text-slate-400 hover:text-slate-700 -mt-1"><X className="h-5 w-5" /></button>
                </div>
                <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Data real da entrega *</label>
                <input type="date" value={data} onChange={(e) => setData(e.target.value)}
                    data-testid="entrega-data-input"
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#0F2044] focus:outline-none" autoFocus />
                <p className="text-[11px] text-slate-500 mt-2">A data informada será usada no Histórico e no campo "Entrega do produto" — permite registrar entregas retroativas.</p>
                {error && <div className="text-xs text-red-600 font-medium mt-2" data-testid="entrega-error">{error}</div>}
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} data-testid="entrega-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={submit} disabled={saving} data-testid="entrega-confirm"
                        className="text-sm px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4" /> {saving ? "Salvando…" : "Confirmar entrega"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// -------------- USO ------------------------------------------
function UsoTab({ contract, usage, onUpdate }) {
    const [form, setForm] = useState({ mes_ref: toCurrentMonthRef(), mensagens: 0, documentos: 0, analises: 0, observacoes: "" });
    const [saving, setSaving] = useState(false);
    const [retoolReady, setRetoolReady] = useState(false);
    const [editingUsage, setEditingUsage] = useState(null);  // M2

    useEffect(() => {
        api.get("/settings").then(r => setRetoolReady(Boolean(r.data.retool_url && r.data.retool_token))).catch(() => {});
    }, []);

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/contracts/${contract.id}/usage`, {
                ...form,
                mensagens: Number(form.mensagens), documentos: Number(form.documentos), analises: Number(form.analises),
            });
            await onUpdate();
            setForm({ mes_ref: toCurrentMonthRef(), mensagens: 0, documentos: 0, analises: 0, observacoes: "" });
        } finally { setSaving(false); }
    }

    async function deleteUsage(u) {  // M2
        if (!window.confirm(`Excluir os dados de uso de ${monthLabel(u.mes_ref)}? Esta ação não pode ser desfeita.`)) return;
        await api.delete(`/contracts/${contract.id}/usage/${u.id}`);
        await onUpdate();
    }

    const chartData = usage.map(u => ({
        label: monthLabel(u.mes_ref),
        Mensagens: u.mensagens, Documentos: u.documentos, Analisador: u.analises,
    }));

    const lastUsage = usage[usage.length - 1];
    const lastPct = lastUsage ? lastUsage.percentual : null;
    const lastVar = lastUsage ? lastUsage.variacao : null;
    const alerts = [];
    if (lastUsage) {
        if (lastVar !== null && lastVar <= -30) alerts.push({ level: "critico", msg: `Uso caiu ${Math.abs(lastVar).toFixed(1)}% vs mês anterior` });
        if (lastUsage.mensagens === 0) alerts.push({ level: "critico", msg: "Módulo Mensagens zerou no mês" });
        if (lastUsage.documentos === 0) alerts.push({ level: "critico", msg: "Módulo Documentos zerou no mês" });
        if (lastUsage.analises === 0) alerts.push({ level: "critico", msg: "Módulo Analisador zerou no mês" });
        if (lastPct !== null && lastPct < 1) alerts.push({ level: "atencao", msg: "Uso de créditos abaixo de 1%" });
    }

    return (
        <div className="space-y-6">
            <div className={`px-4 py-3 rounded-md text-sm flex items-start gap-3 ${retoolReady ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-amber-50 border border-amber-200 text-amber-900"}`} data-testid="retool-banner">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div className="flex-1 text-xs">
                    {retoolReady ? (
                        <>✓ Integração com Retool configurada. Os dados de uso podem ser puxados automaticamente da licito.guru. (sincronização automática será habilitada na próxima fase)</>
                    ) : (
                        <>Integração com Retool ainda não configurada. Lance os dados manualmente abaixo enquanto isso. Configure em <Link to="/configuracoes" className="underline font-semibold">Configurações → Integração Retool</Link>.</>
                    )}
                </div>
            </div>

            {alerts.length > 0 && (
                <div className="space-y-2" data-testid="usage-alerts">
                    {alerts.map((a, i) => (
                        <div key={i} className={`px-4 py-2.5 rounded-md text-sm flex items-center gap-2 ${a.level === "critico" ? "bg-red-50 border border-red-200 text-red-800" : "bg-amber-50 border border-amber-200 text-amber-800"}`}>
                            <AlertCircle className="h-4 w-4" /> {a.msg}
                        </div>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-3 gap-6">
                <Card className="col-span-2" title="Evolução por módulo" testid="card-usage-chart">
                    {chartData.length === 0 ? (
                        <div className="text-sm text-slate-500 py-6 text-center">Sem dados de uso. Insira o primeiro mês ao lado.</div>
                    ) : (
                        <div className="h-64">
                            <ResponsiveContainer>
                                <LineChart data={chartData}>
                                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Line type="monotone" dataKey="Mensagens" stroke="#0F2044" strokeWidth={2} />
                                    <Line type="monotone" dataKey="Documentos" stroke="#3B82F6" strokeWidth={2} />
                                    <Line type="monotone" dataKey="Analisador" stroke="#22C55E" strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </Card>

                <Card title="Inserir uso mensal" testid="card-usage-form">
                    <form onSubmit={submit} className="space-y-3" data-testid="usage-form">
                        <Field label="Mês de referência">
                            <input type="month" required value={form.mes_ref} onChange={(e) => setForm({ ...form, mes_ref: e.target.value })}
                                data-testid="usage-mes-input" className="input" />
                        </Field>
                        <div className="grid grid-cols-3 gap-2">
                            <Field label="Mensagens"><input type="number" min={0} value={form.mensagens} onChange={(e) => setForm({ ...form, mensagens: e.target.value })} data-testid="usage-msg-input" className="input" /></Field>
                            <Field label="Documentos"><input type="number" min={0} value={form.documentos} onChange={(e) => setForm({ ...form, documentos: e.target.value })} data-testid="usage-doc-input" className="input" /></Field>
                            <Field label="Analisador"><input type="number" min={0} value={form.analises} onChange={(e) => setForm({ ...form, analises: e.target.value })} data-testid="usage-an-input" className="input" /></Field>
                        </div>
                        <Field label="Observações">
                            <textarea rows={2} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="usage-obs-input" className="input" />
                        </Field>
                        <button type="submit" disabled={saving} data-testid="usage-submit-button" className="w-full bg-[#0F2044] hover:bg-[#172E5E] text-white text-sm font-semibold py-2 rounded-md disabled:opacity-60">
                            {saving ? "Salvando…" : "Registrar mês"}
                        </button>
                    </form>
                </Card>
            </div>

            <Card title="Tabela mensal" testid="card-usage-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase tracking-[0.12em] text-slate-500 border-b border-slate-100">
                            <th className="py-2 pr-3 font-medium">Mês</th>
                            <th className="py-2 pr-3 font-medium text-right">Créditos</th>
                            <th className="py-2 pr-3 font-medium text-right">Mensagens</th>
                            <th className="py-2 pr-3 font-medium text-right">Documentos</th>
                            <th className="py-2 pr-3 font-medium text-right">Analisador</th>
                            <th className="py-2 pr-3 font-medium text-right">Total</th>
                            <th className="py-2 pr-3 font-medium text-right">% Uso</th>
                            <th className="py-2 pr-3 font-medium text-right">Var. m-1</th>
                            <th className="py-2 pr-3 font-medium">Observações</th>
                            <th className="py-2 font-medium text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {usage.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-slate-500">Sem dados ainda.</td></tr>}
                        {usage.map((u) => (
                            <tr key={u.id} className="border-b border-slate-50 group" data-testid={`usage-row-${u.mes_ref}`}>
                                <td className="py-2 pr-3 font-medium text-[#0F2044]">{monthLabel(u.mes_ref)}</td>
                                <td className="py-2 pr-3 text-right font-mono-tab">{u.creditos_disponiveis}</td>
                                <td className="py-2 pr-3 text-right font-mono-tab">{u.mensagens}</td>
                                <td className="py-2 pr-3 text-right font-mono-tab">{u.documentos}</td>
                                <td className="py-2 pr-3 text-right font-mono-tab">{u.analises}</td>
                                <td className="py-2 pr-3 text-right font-mono-tab font-semibold">{u.total}</td>
                                <td className="py-2 pr-3 text-right font-mono-tab">{fmtPct(u.percentual)}</td>
                                <td className={`py-2 pr-3 text-right font-mono-tab ${u.variacao !== null ? (u.variacao >= 0 ? "text-emerald-600" : "text-red-600") : ""}`}>{u.variacao !== null ? fmtPct(u.variacao, { signed: true }) : "—"}</td>
                                <td className="py-2 pr-3 text-slate-600">{u.observacoes || "—"}</td>
                                <td className="py-2 text-right whitespace-nowrap">
                                    <button onClick={() => setEditingUsage(u)} data-testid={`usage-edit-${u.mes_ref}`}
                                        title="Editar" className="p-1.5 rounded text-slate-400 hover:text-[#0F2044] hover:bg-slate-100">
                                        <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                    <button onClick={() => deleteUsage(u)} data-testid={`usage-delete-${u.mes_ref}`}
                                        title="Excluir" className="p-1.5 rounded text-slate-300 hover:text-red-600 hover:bg-red-50">
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            {editingUsage && (
                <EditUsageModal contract={contract} usage={editingUsage}
                    onClose={() => setEditingUsage(null)}
                    onSaved={async () => { setEditingUsage(null); await onUpdate(); }} />
            )}
        </div>
    );
}

// M2 — modal de edição de um registro de uso mensal
function EditUsageModal({ contract, usage, onClose, onSaved }) {
    const [form, setForm] = useState({
        mensagens: usage.mensagens, documentos: usage.documentos, analises: usage.analises, observacoes: usage.observacoes || "",
    });
    const [saving, setSaving] = useState(false);
    async function submit() {
        setSaving(true);
        try {
            await api.patch(`/contracts/${contract.id}/usage/${usage.id}`, {
                mensagens: Number(form.mensagens), documentos: Number(form.documentos),
                analises: Number(form.analises), observacoes: form.observacoes,
            });
            await onSaved();
        } finally { setSaving(false); }
    }
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="edit-usage-modal" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 fade-in" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <h3 className="font-display font-semibold text-lg text-[#0F2044]">Editar uso — {monthLabel(usage.mes_ref)}</h3>
                    <button onClick={onClose} data-testid="edit-usage-close" className="text-slate-400 hover:text-slate-700 -mt-1"><X className="h-5 w-5" /></button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Mensagens"><input type="number" min={0} value={form.mensagens} onChange={(e) => setForm({ ...form, mensagens: e.target.value })} data-testid="edit-usage-msg" className="input" /></Field>
                    <Field label="Documentos"><input type="number" min={0} value={form.documentos} onChange={(e) => setForm({ ...form, documentos: e.target.value })} data-testid="edit-usage-doc" className="input" /></Field>
                    <Field label="Analisador"><input type="number" min={0} value={form.analises} onChange={(e) => setForm({ ...form, analises: e.target.value })} data-testid="edit-usage-an" className="input" /></Field>
                </div>
                <div className="mt-3"><Field label="Observações"><textarea rows={2} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="edit-usage-obs" className="input" /></Field></div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="text-sm px-4 py-2 border border-slate-300 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={submit} disabled={saving} data-testid="edit-usage-save"
                        className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white rounded-md font-semibold disabled:opacity-60">
                        {saving ? "Salvando…" : "Salvar alterações"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// -------------- JORNADA ------------------------------------------
const RESP_GROUPS = ["Comercial", "Suporte", "Jurídico Cliente", "Jurídico Produto", "Desenvolvimento"];

function JornadaTab({ contract, tasks, onUpdate }) {
    const [completing, setCompleting] = useState(null);
    const [completionNote, setCompletionNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [welcomeOpen, setWelcomeOpen] = useState(false);
    const [jornadaSum, setJornadaSum] = useState(null);
    const [selected, setSelected] = useState(new Set());  // #8 seleção múltipla
    const [bulkSaving, setBulkSaving] = useState(false);
    const [statusFilter, setStatusFilter] = useState(null);  // M3 — filtro pelos cards do resumo
    const [highlightId, setHighlightId] = useState(null);    // M3 — destaque da próxima tarefa
    const [addOpen, setAddOpen] = useState(false);           // M1 — modal adicionar tarefa
    const [editingTask, setEditingTask] = useState(null);    // M12 — modal editar parâmetros
    const [deleteMode, setDeleteMode] = useState(false);     // M3 — exclusão em lote
    const [addMeetingOpen, setAddMeetingOpen] = useState(false);   // M6/M18 — adicionar atividade (reunião/treinamento)
    const [completingMeeting, setCompletingMeeting] = useState(null); // M6 — concluir reunião da jornada
    const [completingTreino, setCompletingTreino] = useState(null);   // M18 — concluir treinamento da jornada

    useEffect(() => {
        api.get(`/contracts/${contract.id}/jornada-summary`).then(r => setJornadaSum(r.data));
    }, [contract.id, tasks]);

    // Ao atualizar as tasks, limpa seleção de tarefas que sumiram ou já concluídas
    useEffect(() => {
        setSelected(prev => {
            const next = new Set();
            const validIds = new Set(tasks.filter(t => t.computed_status !== "concluido" && t.computed_status !== "nao_se_aplica" && t.computed_status !== "bloqueado").map(t => t.id));
            prev.forEach(id => { if (validIds.has(id)) next.add(id); });
            return next;
        });
    }, [tasks]);

    // M3 — filtros por status disparados pelos cards do resumo
    const FILTER_MATCH = {
        concluidas: (t) => t.computed_status === "concluido",
        aguardando: (t) => t.computed_status === "pendente" || t.computed_status === "em_andamento",
        atrasadas: (t) => t.computed_status === "atrasado",
    };
    const FILTER_LABELS = { concluidas: "Concluídas", aguardando: "Aguardando prazo", atrasadas: "Atrasadas" };

    const groupedByResp = useMemo(() => {
        const source = statusFilter ? tasks.filter(FILTER_MATCH[statusFilter]) : tasks;
        const g = {};
        source.forEach(t => {
            (g[t.responsavel] = g[t.responsavel] || []).push(t);
        });
        // M9 — ordena cronologicamente por data prevista/prazo dentro de cada grupo (sem prazo vai ao fim)
        const sortByDate = (a, b) => {
            const da = a.predicted_date || a.override_date || "";
            const db = b.predicted_date || b.override_date || "";
            if (da && db) return da.localeCompare(db) || (a.ordem - b.ordem);
            if (da && !db) return -1;
            if (!da && db) return 1;
            return a.ordem - b.ordem;
        };
        const out = [];
        RESP_GROUPS.forEach(r => { if (g[r]) out.push({ resp: r, items: g[r].sort(sortByDate) }); });
        Object.keys(g).forEach(r => { if (!RESP_GROUPS.includes(r)) out.push({ resp: r, items: g[r].sort(sortByDate) }); });
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tasks, statusFilter]);

    const filteredCount = useMemo(
        () => (statusFilter ? tasks.filter(FILTER_MATCH[statusFilter]).length : tasks.length),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tasks, statusFilter]
    );

    function toggleFilter(key) {
        setStatusFilter(prev => (prev === key ? null : key));
    }

    // M3 — clicar na "Próxima tarefa" rola até ela e a destaca
    function goToTask(t) {
        if (!t) return;
        setStatusFilter(null);
        setHighlightId(t.id);
        setTimeout(() => {
            document.querySelector(`[data-testid="task-row-${t.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
        setTimeout(() => setHighlightId(null), 3500);
    }

    async function updateTask(t, updates) {
        await api.patch(`/tasks/${t.id}`, updates);
        await onUpdate();
    }

    // M1 — excluir tarefa da jornada
    async function deleteTask(t) {
        if (!window.confirm(`Excluir a tarefa "#${t.ordem} ${t.descricao}" da jornada?\nEsta ação não pode ser desfeita (fica registrada no Histórico).`)) return;
        await api.delete(`/tasks/${t.id}`);
        await onUpdate();
    }

    function handleToggle(t) {
        if (t.computed_status === "bloqueado") return;
        if (t.status === "concluido" || t.status === "nao_se_aplica") {
            updateTask(t, { status: "pendente" });
        } else if (t.activity_type === "treinamento") {
            // M18 — concluir treinamento abre formulário específico
            setCompletingTreino(t);
        } else if (t.is_meeting || t.activity_type === "reuniao") {
            // M6/M18 — concluir reunião da jornada abre o formulário completo de reunião
            setCompletingMeeting(t);
        } else {
            setCompleting(t);
            setCompletionNote(t.observacao || "");
        }
    }

    async function confirmComplete() {
        if (!completing) return;
        setSaving(true);
        try {
            await api.patch(`/tasks/${completing.id}`, { status: "concluido", observacao: completionNote.trim() });
            await onUpdate();
            setCompleting(null);
            setCompletionNote("");
        } finally { setSaving(false); }
    }

    async function confirmNA() {
        if (!completing) return;
        setSaving(true);
        try {
            await api.patch(`/tasks/${completing.id}`, { status: "nao_se_aplica", observacao: completionNote.trim() || "Não se aplica" });
            await onUpdate();
            setCompleting(null);
            setCompletionNote("");
        } finally { setSaving(false); }
    }

    function toggleSelect(tid) {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(tid)) next.delete(tid); else next.add(tid);
            return next;
        });
    }

    async function bulkComplete() {
        if (selected.size === 0) return;
        if (!window.confirm(`Marcar ${selected.size} tarefa(s) como concluída(s)?`)) return;
        setBulkSaving(true);
        try {
            await api.post(`/tasks/bulk-complete`, { task_ids: Array.from(selected), observacao: "Concluído em lote pela jornada" });
            setSelected(new Set());
            await onUpdate();
        } finally { setBulkSaving(false); }
    }

    // M3 — exclusão de tarefas em lote (com confirmação)
    async function bulkDelete() {
        if (selected.size === 0) return;
        if (!window.confirm(`Excluir permanentemente ${selected.size} tarefa(s) da jornada?\nEsta ação não pode ser desfeita (fica registrada no Histórico).`)) return;
        setBulkSaving(true);
        try {
            await api.post(`/tasks/bulk-delete`, { task_ids: Array.from(selected) });
            setSelected(new Set());
            setDeleteMode(false);
            await onUpdate();
        } finally { setBulkSaving(false); }
    }

    return (
        <div className="space-y-6">
            {/* Painel gerencial de resumo da jornada */}
            {jornadaSum && (
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="jornada-summary-panel">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Resumo da jornada</div>
                            <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-0.5">
                                {jornadaSum.progresso_pct}% concluído · etapa atual: {FASE_LABELS[jornadaSum.fase_atual]}
                            </h3>
                        </div>
                        <div className="text-xs text-slate-500">Dia {jornadaSum.dias_desde_assinatura} de 360</div>
                    </div>

                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
                        <div className="h-full bg-emerald-500" style={{ width: `${jornadaSum.progresso_pct}%` }} />
                    </div>

                    <div className="grid grid-cols-5 gap-4">
                        <Mini label="Concluídas" value={jornadaSum.concluidas} color="#10B981" testid="jornada-done"
                            onClick={() => toggleFilter("concluidas")} active={statusFilter === "concluidas"} />
                        <Mini label="Aguardando prazo" value={jornadaSum.aguardando_prazo ?? jornadaSum.pendentes} color="#3B82F6" testid="jornada-pending"
                            onClick={() => toggleFilter("aguardando")} active={statusFilter === "aguardando"} />
                        <Mini label="Atrasadas" value={jornadaSum.atrasadas} color="#EF4444" testid="jornada-overdue"
                            onClick={() => toggleFilter("atrasadas")} active={statusFilter === "atrasadas"} />
                        <Mini label="Próxima tarefa" value={jornadaSum.next_task?.descricao?.slice(0, 36) + (jornadaSum.next_task?.descricao?.length > 36 ? "…" : "") || "—"} color="#0F2044" small testid="jornada-next"
                            onClick={jornadaSum.next_task ? () => goToTask(jornadaSum.next_task) : undefined} />
                        <Mini label="Prazo da próxima" value={jornadaSum.next_task?.predicted_date ? brl(jornadaSum.next_task.predicted_date) : "—"} color="#0F2044" small testid="jornada-next-date"
                            onClick={jornadaSum.next_task ? () => goToTask(jornadaSum.next_task) : undefined} />
                    </div>
                    {(jornadaSum.nao_se_aplica || 0) > 0 && (
                        <div className="mt-2 text-[11px] text-slate-500">{jornadaSum.nao_se_aplica} tarefa(s) marcadas como <strong>Não se aplica</strong>.</div>
                    )}
                    {jornadaSum.next_task && (
                        <div className="mt-4 flex items-center gap-3 text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-md px-3 py-2">
                            <span><strong>Responsável:</strong> {jornadaSum.next_task.responsavel}</span>
                            <span>·</span>
                            <span><strong>Dependência:</strong> {jornadaSum.next_task_dependencia === "cliente" ? "Cliente (aguardando ação dele)" : "Interna (time licito.guru)"}</span>
                        </div>
                    )}
                </div>
            )}

            {/* M1 + M3 — barra de filtro ativo e botão de adicionar tarefa */}
            <div className="flex items-center justify-between gap-3">
                {statusFilter ? (
                    <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 border border-slate-200 rounded-md px-3 py-1.5" data-testid="jornada-filter-bar">
                        <span>Filtrando: <strong className="text-[#0F2044]">{FILTER_LABELS[statusFilter]}</strong> · {filteredCount} tarefa(s)</span>
                        <button onClick={() => setStatusFilter(null)} data-testid="jornada-filter-clear"
                            className="text-xs font-semibold text-[#0F2044] hover:underline inline-flex items-center gap-1">
                            <X className="h-3 w-3" /> Limpar filtro
                        </button>
                    </div>
                ) : <span />}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => setAddMeetingOpen(true)} data-testid="add-meeting-task-button"
                        className="text-sm font-semibold border border-[#0F2044] text-[#0F2044] hover:bg-[#0F2044]/5 px-4 py-2 rounded-md inline-flex items-center gap-1.5">
                        <Coffee className="h-4 w-4" /> Adicionar atividade
                    </button>
                    <button onClick={() => setAddOpen(true)} data-testid="add-task-button"
                        className="text-sm font-semibold bg-[#0F2044] hover:bg-[#172E5E] text-white px-4 py-2 rounded-md inline-flex items-center gap-1.5">
                        <Plus className="h-4 w-4" /> Adicionar tarefa
                    </button>
                </div>
            </div>

            {/* Barra de ação em lote (#8 + M3) */}
            {selected.size > 0 && !deleteMode && (
                <div className="sticky top-4 z-10 bg-emerald-600 text-white border border-emerald-700 rounded-md px-4 py-3 shadow-lg flex items-center gap-3" data-testid="bulk-bar">
                    <CheckSquare className="h-4 w-4" />
                    <div className="font-semibold text-sm">{selected.size} tarefa(s) selecionada(s)</div>
                    <button
                        onClick={bulkComplete}
                        disabled={bulkSaving}
                        data-testid="bulk-complete-button"
                        className="ml-auto text-sm font-semibold px-3 py-1.5 rounded bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                    >
                        {bulkSaving ? "Concluindo…" : "Concluir selecionadas"}
                    </button>
                    <button
                        onClick={() => setDeleteMode(true)}
                        data-testid="bulk-delete-mode-button"
                        className="text-sm font-semibold px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-1.5"
                    >
                        <Trash2 className="h-3.5 w-3.5" /> Excluir selecionadas
                    </button>
                    <button
                        onClick={() => setSelected(new Set())}
                        data-testid="bulk-clear-button"
                        className="text-xs font-semibold px-3 py-1.5 rounded border border-white/40 hover:bg-emerald-700"
                    >
                        Limpar
                    </button>
                </div>
            )}

            {/* M3 — barra vermelha de confirmação da exclusão em lote */}
            {selected.size > 0 && deleteMode && (
                <div className="sticky top-4 z-10 bg-red-600 text-white border border-red-700 rounded-md px-4 py-3 shadow-lg flex items-center gap-3" data-testid="bulk-delete-bar">
                    <Trash2 className="h-4 w-4" />
                    <div className="font-semibold text-sm">Será excluída {selected.size} tarefa(s) selecionada(s). Esta ação não pode ser desfeita.</div>
                    <button
                        onClick={() => setDeleteMode(false)}
                        data-testid="bulk-delete-cancel"
                        className="ml-auto text-xs font-semibold px-3 py-1.5 rounded border border-white/40 hover:bg-red-700"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={bulkDelete}
                        disabled={bulkSaving}
                        data-testid="bulk-delete-confirm"
                        className="text-sm font-semibold px-3 py-1.5 rounded bg-white text-red-700 hover:bg-red-50 disabled:opacity-60"
                    >
                        {bulkSaving ? "Excluindo…" : "Excluir selecionadas"}
                    </button>
                </div>
            )}

            {/* Tarefas agrupadas por responsável */}
            {groupedByResp.map((g) => (
                <Card key={g.resp} title={g.resp}
                    subtitle={`${g.items.filter(i => i.computed_status === "concluido").length} de ${g.items.length} concluídas`}
                    testid={`card-resp-${g.resp}`}>
                    <ul className="divide-y divide-slate-100">
                        {g.items.map((t) => {
                            const isWelcome = t.ordem === 1;
                            const canSelect = t.computed_status !== "concluido" && t.computed_status !== "nao_se_aplica" && t.computed_status !== "bloqueado";
                            const isSelected = selected.has(t.id);
                            return (
                                <li key={t.id} className={`py-3 flex items-start gap-3 transition-colors ${t.computed_status === "bloqueado" ? "opacity-50" : ""} ${isSelected ? (deleteMode ? "bg-red-50 ring-1 ring-red-300 rounded-md px-2 -mx-2" : "bg-emerald-50/40") : ""} ${highlightId === t.id ? "bg-amber-50 ring-2 ring-amber-400 rounded-md px-2 -mx-2" : ""}`} data-testid={`task-row-${t.id}`}>
                                    {canSelect && (
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleSelect(t.id)}
                                            data-testid={`task-select-${t.id}`}
                                            title="Selecionar para concluir em lote"
                                            className="mt-1.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                        />
                                    )}
                                    {!canSelect && <span className="w-4" />}
                                    <button
                                        onClick={() => handleToggle(t)}
                                        disabled={t.computed_status === "bloqueado"}
                                        data-testid={`task-toggle-${t.id}`}
                                        className="mt-0.5 flex-shrink-0"
                                        title={t.computed_status === "bloqueado" ? "Aguardando data DP1" : (t.status === "concluido" || t.status === "nao_se_aplica" ? "Reabrir tarefa" : "Marcar como concluída")}
                                    >
                                        <TaskStatusIcon status={t.computed_status} large />
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className={`text-sm font-medium ${t.computed_status === "concluido" || t.computed_status === "nao_se_aplica" ? "line-through text-slate-400" : "text-[#0F2044]"}`}>
                                            <span className="text-xs text-slate-400 mr-2 font-mono-tab">#{t.ordem}</span>
                                            {t.descricao}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                                            <span><strong>Fase:</strong> {FASE_LABELS[t.fase]}</span>
                                            <span>Prazo: {t.prazo_label}</span>
                                            {t.predicted_date && <span className={`font-mono-tab ${t.computed_status === "atrasado" ? "text-red-600 font-semibold" : ""}`}>({brl(t.predicted_date)})</span>}
                                            {t.computed_status === "atrasado" && <span className="badge-status badge-critico">Atrasada</span>}
                                            {t.computed_status === "nao_se_aplica" && <span className="badge-status badge-neutral">Não se aplica</span>}
                                            {t.computed_status === "concluido" && t.data_concluida && <span className="text-emerald-600">Concluída em {brl(t.data_concluida)}</span>}
                                            {t.recorrente && <span className="badge-status badge-neutral">Recorrente</span>}
                                            {t.is_meeting && (
                                                <span className="badge-status inline-flex items-center gap-1" style={{ background: "#DBEAFE", color: "#1D4ED8" }} data-testid={`task-meeting-badge-${t.id}`}>
                                                    <Coffee className="h-2.5 w-2.5" /> Reunião
                                                </span>
                                            )}
                                            {t.activity_type === "treinamento" && (
                                                <span className="badge-status inline-flex items-center gap-1" style={{ background: "#EDE9FE", color: "#6D28D9" }} data-testid={`task-treino-badge-${t.id}`}>
                                                    <GraduationCap className="h-2.5 w-2.5" /> Treinamento
                                                </span>
                                            )}
                                            {t.custom && !t.is_meeting && <span className="badge-status" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>Personalizada</span>}
                                        </div>
                                        {t.observacao && (
                                            <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-md px-2.5 py-1.5">
                                                <strong className="text-slate-500">Anotação:</strong> {t.observacao}
                                            </div>
                                        )}
                                        {isWelcome && t.computed_status !== "concluido" && t.computed_status !== "nao_se_aplica" && (
                                            <button onClick={() => setWelcomeOpen(true)} data-testid="open-welcome-button"
                                                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold bg-[#0F2044] hover:bg-[#172E5E] text-white px-3 py-1.5 rounded-md">
                                                <Send className="h-3 w-3" /> Enviar Boas-vindas
                                            </button>
                                        )}
                                    </div>
                                    <button onClick={() => setEditingTask(t)} data-testid={`task-edit-${t.id}`}
                                        title="Editar parâmetros da tarefa"
                                        className="mt-0.5 flex-shrink-0 text-slate-300 hover:text-[#0F2044] transition-colors">
                                        <Pencil className="h-4 w-4" />
                                    </button>
                                    <button onClick={() => deleteTask(t)} data-testid={`task-delete-${t.id}`}
                                        title="Excluir tarefa da jornada"
                                        className="mt-0.5 flex-shrink-0 text-slate-300 hover:text-red-600 transition-colors">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </Card>
            ))}

            {completing && (
                <CompleteTaskModal
                    task={completing}
                    note={completionNote}
                    onChangeNote={setCompletionNote}
                    onConfirm={confirmComplete}
                    onConfirmNA={confirmNA}
                    onCancel={() => setCompleting(null)}
                    saving={saving}
                />
            )}
            {welcomeOpen && <WelcomeEmailModal contract={contract} onClose={() => setWelcomeOpen(false)} onSent={async () => { setWelcomeOpen(false); await onUpdate(); }} />}
            {addOpen && <AddTaskModal contract={contract} onClose={() => setAddOpen(false)} onSaved={async () => { setAddOpen(false); await onUpdate(); }} />}
            {addMeetingOpen && <AddTaskModal contract={contract} activity onClose={() => setAddMeetingOpen(false)} onSaved={async () => { setAddMeetingOpen(false); await onUpdate(); }} />}
            {completingMeeting && (
                <CompleteMeetingTaskModal contract={contract} task={completingMeeting}
                    onClose={() => setCompletingMeeting(null)}
                    onSaved={async () => { setCompletingMeeting(null); await onUpdate(); }} />
            )}
            {completingTreino && (
                <CompleteTreinamentoTaskModal contract={contract} task={completingTreino}
                    onClose={() => setCompletingTreino(null)}
                    onSaved={async () => { setCompletingTreino(null); await onUpdate(); }} />
            )}
            {editingTask && <EditTaskModal task={editingTask} onClose={() => setEditingTask(null)} onSaved={async () => { setEditingTask(null); await onUpdate(); }} />}
        </div>
    );
}


// M12 — modal de edição total de parâmetros e datas da tarefa
function EditTaskModal({ task, onClose, onSaved }) {
    const [descricao, setDescricao] = useState(task.descricao || "");
    const [fase, setFase] = useState(task.fase || "adocao");
    const [responsavel, setResponsavel] = useState(task.responsavel || "Jurídico Cliente");
    const [prazo, setPrazo] = useState(task.override_date || task.predicted_date || "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    async function submit() {
        if (!descricao.trim()) { setError("A descrição não pode ficar vazia."); return; }
        setSaving(true); setError("");
        try {
            const payload = { descricao: descricao.trim(), fase, responsavel };
            if (prazo && prazo !== task.predicted_date) payload.override_date = prazo;
            await api.patch(`/tasks/${task.id}`, payload);
            await onSaved();
        } catch (e) {
            setError(e?.response?.data?.detail || "Erro ao salvar a tarefa.");
            setSaving(false);
        }
    }

    async function resetPrazo() {
        setSaving(true); setError("");
        try {
            await api.patch(`/tasks/${task.id}`, { override_date: "" });
            await onSaved();
        } catch (e) {
            setError("Erro ao restaurar o prazo padrão.");
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="edit-task-modal">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 fade-in">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#0F2044] font-semibold">Editar tarefa #{task.ordem}</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">Parâmetros e datas</h3>
                    </div>
                    <button onClick={onClose} data-testid="edit-task-close" className="text-slate-400 hover:text-slate-700 -mt-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="space-y-4">
                    <Field label="Descrição *">
                        <textarea rows={3} value={descricao} onChange={(e) => setDescricao(e.target.value)}
                            data-testid="edit-task-descricao" className="input" autoFocus />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Fase">
                            <select value={fase} onChange={(e) => setFase(e.target.value)} data-testid="edit-task-fase" className="input">
                                {Object.entries(FASE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </Field>
                        <Field label="Responsável">
                            <select value={responsavel} onChange={(e) => setResponsavel(e.target.value)} data-testid="edit-task-responsavel" className="input">
                                {[...new Set([...RESP_GROUPS, task.responsavel])].filter(Boolean).map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label="Data de prazo">
                        <div className="flex items-center gap-3">
                            <input type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)}
                                data-testid="edit-task-prazo" className="input flex-1" />
                            {task.override_date && (
                                <button onClick={resetPrazo} disabled={saving} data-testid="edit-task-reset-prazo"
                                    className="text-xs text-slate-500 hover:text-[#0F2044] underline whitespace-nowrap disabled:opacity-50">
                                    Voltar ao prazo padrão
                                </button>
                            )}
                        </div>
                    </Field>
                    {error && <div className="text-xs text-red-600 font-medium" data-testid="edit-task-error">{error}</div>}
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} data-testid="edit-task-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={submit} disabled={saving} data-testid="edit-task-save"
                        className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white rounded-md font-semibold disabled:opacity-60">
                        {saving ? "Salvando…" : "Salvar alterações"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Mini({ label, value, color, small, testid, onClick, active }) {
    const clickable = typeof onClick === "function";
    const Comp = clickable ? "button" : "div";
    return (
        <Comp
            onClick={onClick}
            data-testid={testid}
            className={`text-left border rounded-md p-3 w-full transition-all ${active ? "border-[#0F2044] ring-1 ring-[#0F2044]/40 bg-slate-50" : "border-slate-100"} ${clickable ? "cursor-pointer hover:border-slate-300 hover:shadow-sm" : ""}`}
        >
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
            <div className={`mt-1 font-display font-semibold ${small ? "text-sm" : "text-2xl"}`} style={{ color }}>{value}</div>
            {clickable && (
                <div className="text-[9px] text-slate-400 mt-0.5">{active ? "Filtro ativo · clique p/ limpar" : "Clique para ver"}</div>
            )}
        </Comp>
    );
}

// M1/M18 — modal de inclusão de item na jornada (tarefa comum ou atividade Reunião/Treinamento)
function AddTaskModal({ contract, onClose, onSaved, isMeeting = false, activity = false }) {
    const [descricao, setDescricao] = useState("");
    const [fase, setFase] = useState("adocao");
    const [responsavel, setResponsavel] = useState("Jurídico Cliente");
    const [dataPrazo, setDataPrazo] = useState("");
    const [tipoAtividade, setTipoAtividade] = useState("reuniao"); // M18 — reuniao | treinamento
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const isTreino = activity && tipoAtividade === "treinamento";

    async function submit() {
        if (!descricao.trim() || !dataPrazo) {
            setError("Preencha a descrição e a data de prazo.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const activity_type = activity ? tipoAtividade : (isMeeting ? "reuniao" : "");
            const is_meeting = activity ? (tipoAtividade === "reuniao") : isMeeting;
            await api.post(`/contracts/${contract.id}/tasks`, {
                descricao: descricao.trim(), fase, responsavel, data_prazo: dataPrazo, is_meeting, activity_type,
            });
            await onSaved();
        } catch (e) {
            setError(e?.response?.data?.detail || "Erro ao adicionar a tarefa.");
            setSaving(false);
        }
    }

    const headerTitle = activity ? "Nova atividade na jornada" : (isMeeting ? "Nova reunião na jornada" : "Nova tarefa personalizada");
    const descPlaceholder = isTreino
        ? "Ex.: Treinamento do módulo Analisador com a equipe do cliente"
        : (activity || isMeeting) ? "Ex.: Reunião de acompanhamento mensal com o fiscal do contrato"
        : "Ex.: Reunião extra de alinhamento sobre módulo Analisador";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="add-task-modal">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 fade-in">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#0F2044] font-semibold">Incluir item na jornada</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">{headerTitle}</h3>
                        <div className="text-xs text-slate-500 mt-1">{contract.organizacao_nome}</div>
                    </div>
                    <button onClick={onClose} data-testid="add-task-modal-close" className="text-slate-400 hover:text-slate-700 -mt-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="space-y-4">
                    {activity && (
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Tipo da atividade</label>
                            <div className="flex rounded-md border border-slate-300 overflow-hidden">
                                <button type="button" onClick={() => setTipoAtividade("reuniao")} data-testid="activity-type-reuniao"
                                    className={`flex-1 py-2 text-sm font-semibold inline-flex items-center justify-center gap-1.5 ${tipoAtividade === "reuniao" ? "bg-[#0F2044] text-white" : "bg-white text-slate-600"}`}>
                                    <Coffee className="h-4 w-4" /> Reunião
                                </button>
                                <button type="button" onClick={() => setTipoAtividade("treinamento")} data-testid="activity-type-treinamento"
                                    className={`flex-1 py-2 text-sm font-semibold inline-flex items-center justify-center gap-1.5 ${tipoAtividade === "treinamento" ? "bg-violet-700 text-white" : "bg-white text-slate-600"}`}>
                                    <GraduationCap className="h-4 w-4" /> Treinamento
                                </button>
                            </div>
                        </div>
                    )}
                    <Field label={isTreino ? "Descrição do treinamento *" : (activity || isMeeting) ? "Descrição da reunião *" : "Descrição da tarefa *"}>
                        <textarea rows={3} value={descricao} onChange={(e) => setDescricao(e.target.value)}
                            placeholder={descPlaceholder}
                            data-testid="add-task-descricao-input" className="input" autoFocus />
                    </Field>
                    {(activity || isMeeting) && (
                        <p className="text-[11px] text-slate-500 -mt-2">
                            Ao concluir esta {isTreino ? "atividade" : "reunião"} na jornada, o formulário obrigatório ({isTreino ? "duração, módulos, participantes, clima, notas e próximos passos" : "clima, participantes, notas e próximos passos"}) será aberto e o registro entrará automaticamente na aba "Reuniões e Clima".
                        </p>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Fase">
                            <select value={fase} onChange={(e) => setFase(e.target.value)} data-testid="add-task-fase-select" className="input">
                                {Object.entries(FASE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </Field>
                        <Field label="Responsável">
                            <select value={responsavel} onChange={(e) => setResponsavel(e.target.value)} data-testid="add-task-responsavel-select" className="input">
                                {RESP_GROUPS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label="Data de prazo *">
                        <input type="date" value={dataPrazo} onChange={(e) => setDataPrazo(e.target.value)}
                            data-testid="add-task-prazo-input" className="input" />
                    </Field>
                    {error && <div className="text-xs text-red-600 font-medium" data-testid="add-task-error">{error}</div>}
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} data-testid="add-task-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={submit} disabled={saving} data-testid="add-task-submit"
                        className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white rounded-md font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                        <Plus className="h-4 w-4" /> {saving ? "Adicionando…" : "Adicionar à jornada"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CompleteTaskModal({ task, note, onChangeNote, onConfirm, onConfirmNA, onCancel, saving }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="task-complete-modal">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 fade-in">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-600 font-semibold">Concluir tarefa</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">{task.descricao}</h3>
                        <div className="text-xs text-slate-500 mt-1">#{task.ordem} · {task.responsavel} · {task.prazo_label}</div>
                    </div>
                    <button onClick={onCancel} data-testid="task-modal-close" className="text-slate-400 hover:text-slate-700 -mt-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <Field label="Anotação / Registro do que foi feito">
                    <textarea rows={5} value={note} onChange={(e) => onChangeNote(e.target.value)}
                        placeholder="Ex.: enviado e-mail de boas-vindas. Confirmado recebimento. Próximo passo: agendar reunião na semana que vem."
                        data-testid="task-modal-note-input" className="input" autoFocus />
                </Field>
                <p className="text-[11px] text-slate-500 mt-2">A anotação é registrada no <strong>Histórico</strong> do cliente.</p>
                <div className="flex flex-wrap justify-end gap-2 mt-5">
                    <button onClick={onCancel} data-testid="task-modal-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button
                        onClick={onConfirmNA}
                        disabled={saving}
                        data-testid="task-modal-na"
                        title="Marcar como Não se aplica"
                        className="text-sm px-4 py-2 border border-slate-400 bg-white hover:bg-slate-50 text-slate-700 font-semibold rounded-md inline-flex items-center gap-1 disabled:opacity-60"
                    >
                        <MinusCircle className="h-4 w-4" /> Não se aplica
                    </button>
                    <button onClick={onConfirm} disabled={saving} data-testid="task-modal-confirm"
                        className="text-sm px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md inline-flex items-center gap-1 disabled:opacity-60">
                        <CheckCircle2 className="h-4 w-4" /> {saving ? "Concluindo…" : "Concluir e registrar"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function WelcomeEmailModal({ contract, onClose, onSent }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [to, setTo] = useState("");
    const [subject, setSubject] = useState("");
    const [bodyHtml, setBodyHtml] = useState("");
    const [tab, setTab] = useState("editar");

    useEffect(() => {
        api.post(`/contracts/${contract.id}/welcome-email/preview`)
            .then(r => {
                setPreview(r.data);
                setTo(r.data.to);
                setSubject(r.data.subject);
                setBodyHtml(r.data.body_html);
            })
            .catch(e => setError(e?.response?.data?.detail || "Falha ao gerar prévia"))
            .finally(() => setLoading(false));
    }, [contract.id]);

    async function send() {
        setError(""); setSending(true);
        try {
            await api.post(`/contracts/${contract.id}/welcome-email/send`, { to, subject, body_html: bodyHtml });
            onSent();
        } catch (e) {
            setError(e?.response?.data?.detail || "Falha no envio");
        } finally { setSending(false); }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-8" data-testid="welcome-modal">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col fade-in">
                <div className="p-6 pb-3 border-b border-slate-100 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#0F2044] font-semibold">E-mail de Boas-vindas</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">
                            Plano {PLANO_LABELS[contract.plano] || contract.plano} — {contract.organizacao_nome}
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">Template carregado automaticamente. Edite o conteúdo se quiser antes de enviar.</p>
                    </div>
                    <button onClick={onClose} data-testid="welcome-close" className="text-slate-400 hover:text-slate-700">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {loading ? (
                    <div className="p-10 text-center text-sm text-slate-500">Gerando prévia…</div>
                ) : (
                    <>
                        <div className="px-6 pt-3 border-b border-slate-100 flex gap-1">
                            {["editar", "visualizar"].map((t) => (
                                <button key={t} onClick={() => setTab(t)} data-testid={`welcome-tab-${t}`}
                                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? "border-[#0F2044] text-[#0F2044]" : "border-transparent text-slate-500"}`}>
                                    {t === "editar" ? "Editar" : "Visualizar"}
                                </button>
                            ))}
                        </div>

                        <div className="flex-1 overflow-auto p-6 space-y-3">
                            {tab === "editar" ? (
                                <>
                                    <Field label="Para (e-mail)"><input type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="welcome-to" className="input" /></Field>
                                    <Field label="Assunto"><input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="welcome-subject" className="input" /></Field>
                                    <Field label="Corpo (HTML)"><textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} rows={14} data-testid="welcome-body" className="input font-mono text-xs" /></Field>
                                </>
                            ) : (
                                <div className="border border-slate-200 rounded-md p-4 bg-slate-50" data-testid="welcome-preview">
                                    <div className="text-xs text-slate-500 mb-2"><strong>Para:</strong> {to}</div>
                                    <div className="text-xs text-slate-500 mb-3"><strong>Assunto:</strong> {subject}</div>
                                    <div className="bg-white border border-slate-100 rounded p-4" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                                </div>
                            )}
                            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="welcome-error">{error}</div>}
                            {!to && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Sem e-mail no ponto focal — preencha em Visão Geral → Dados cadastrais ou edite acima antes de enviar.</div>}
                        </div>

                        <div className="p-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
                            <button onClick={onClose} data-testid="welcome-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-white">Cancelar</button>
                            <button onClick={send} disabled={sending || !to} data-testid="welcome-send"
                                className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white font-semibold rounded-md inline-flex items-center gap-2 disabled:opacity-60">
                                <Send className="h-4 w-4" /> {sending ? "Enviando…" : "Enviar agora"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// -------------- REUNIÕES + TREINAMENTOS ------------------------------------------
function ReunioesTab({ contract, meetings, onUpdate }) {
    const [form, setForm] = useState({ data: new Date().toISOString().slice(0, 10), tipo: "Acompanhamento", participantes: "", clima: "bom", notas: "", proximos_passos: "" });
    const [saving, setSaving] = useState(false);
    const [treinos, setTreinos] = useState([]);
    const [tForm, setTForm] = useState({ data: new Date().toISOString().slice(0, 10), modulos: [], participantes: "", quantidade_participantes: 1, duracao_minutos: 60, entregue_por: "", material_compartilhado: "", observacoes: "", clima: "bom", notas: "", proximos_passos: "" });
    const [tSaving, setTSaving] = useState(false);
    const [showTreino, setShowTreino] = useState(false);
    const [editingMeeting, setEditingMeeting] = useState(null);  // #5
    const [editingTreino, setEditingTreino] = useState(null);    // M18.2
    const [lastFeedback, setLastFeedback] = useState(null);

    async function refreshT() {
        const r = await api.get(`/contracts/${contract.id}/treinamentos`);
        setTreinos(r.data);
    }
    useEffect(() => { refreshT(); }, [contract.id]);

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        setLastFeedback(null);
        try {
            const { data } = await api.post(`/contracts/${contract.id}/meetings`, form);
            await onUpdate();
            setForm({ ...form, notas: "", proximos_passos: "", participantes: "" });
            setLastFeedback({
                follow_up: !!data?.follow_up_criado,
                acoes: data?.acoes_de_proximos_passos || 0,
            });
        } finally { setSaving(false); }
    }
    async function submitTreino(e) {
        e.preventDefault();
        if (tForm.modulos.length === 0) { window.alert("Selecione ao menos um módulo treinado."); return; }
        setTSaving(true);
        try {
            await api.post(`/contracts/${contract.id}/treinamentos`, {
                ...tForm,
                quantidade_participantes: Number(tForm.quantidade_participantes),
                duracao_minutos: Number(tForm.duracao_minutos),
            });
            await refreshT();
            setTForm({ ...tForm, modulos: [], participantes: "", material_compartilhado: "", observacoes: "", notas: "", proximos_passos: "" });
            setShowTreino(false);
        } finally { setTSaving(false); }
    }
    async function deleteTreino(tid) {
        if (!window.confirm("Excluir este treinamento?")) return;
        await api.delete(`/treinamentos/${tid}`);
        await refreshT();
    }
    async function deleteMeeting(mid) {
        if (!window.confirm("Excluir esta reunião?")) return;
        await api.delete(`/contracts/${contract.id}/meetings/${mid}`);
        await onUpdate();
    }

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-3 gap-6">
                <div className="col-span-2 space-y-4">
                    <Card title={`Histórico de reuniões (${meetings.length})`} testid="card-meetings-history">
                        {meetings.length === 0 ? (
                            <div className="text-sm text-slate-500 py-3">Sem reuniões registradas ainda.</div>
                        ) : (
                            <ul className="divide-y divide-slate-100">
                                {meetings.map((m) => (
                                    <li key={m.id} className="py-3 group" data-testid={`meeting-row-${m.id}`}>
                                        <div className="flex items-center gap-3">
                                            <div className="text-sm font-mono-tab text-slate-500 w-24">{brl(m.data)}</div>
                                            <div className="text-sm font-medium text-[#0F2044]">{m.tipo}</div>
                                            <ClimaBadge clima={m.clima} />
                                            <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => setEditingMeeting(m)}
                                                    data-testid={`meeting-edit-${m.id}`}
                                                    title="Editar reunião"
                                                    className="p-1.5 rounded hover:bg-slate-100 text-slate-600 hover:text-[#0F2044]"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => deleteMeeting(m.id)}
                                                    data-testid={`meeting-delete-${m.id}`}
                                                    title="Excluir reunião"
                                                    className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                        {m.participantes && <div className="text-xs text-slate-500 mt-1">Participantes: {m.participantes}</div>}
                                        {m.notas && <div className="text-sm text-slate-700 mt-1 whitespace-pre-line">{m.notas}</div>}
                                        {m.proximos_passos && <div className="text-xs text-slate-600 mt-1 whitespace-pre-line"><strong>Próximos passos:</strong> {m.proximos_passos}</div>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>
                </div>
                <Card title="Registrar reunião" testid="card-meeting-form">
                    <form onSubmit={submit} className="space-y-3" data-testid="meeting-form">
                        <Field label="Data"><input type="date" required value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="meeting-data-input" className="input" /></Field>
                        <Field label="Tipo"><input type="text" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} data-testid="meeting-tipo-input" className="input" /></Field>
                        <Field label="Participantes"><input type="text" value={form.participantes} onChange={(e) => setForm({ ...form, participantes: e.target.value })} data-testid="meeting-participantes-input" className="input" /></Field>
                        <Field label="Clima da reunião">
                            <select required value={form.clima} onChange={(e) => setForm({ ...form, clima: e.target.value })} data-testid="meeting-clima-input" className="input">
                                {Object.entries(CLIMA_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                            </select>
                        </Field>
                        <Field label="Notas"><textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="meeting-notas-input" className="input" /></Field>
                        <Field label="Próximos passos">
                            <textarea
                                rows={3}
                                value={form.proximos_passos}
                                onChange={(e) => setForm({ ...form, proximos_passos: e.target.value })}
                                data-testid="meeting-proximos-input"
                                placeholder={"Numere os passos para virarem tarefas automáticas (com prazo +5 dias):\n1. Agendar reunião com jurídico\n2. Enviar material de treinamento\n3. Retornar com proposta comercial"}
                                className="input"
                            />
                            <p className="text-[10px] text-slate-500 mt-1">💡 Itens numerados (1., 2., 3-) viram automaticamente Ações no Plano de Ação e aparecem na Agenda.</p>
                        </Field>
                        <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-md px-3 py-2">
                            Ao registrar, o sistema já <strong>agenda um contato de follow-up em 30 dias</strong> automaticamente.
                        </div>
                        <button type="submit" disabled={saving} data-testid="meeting-submit-button" className="w-full bg-[#0F2044] hover:bg-[#172E5E] text-white text-sm font-semibold py-2 rounded-md disabled:opacity-60">
                            {saving ? "Salvando…" : "Registrar reunião"}
                        </button>
                        {lastFeedback && (
                            <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 mt-2" data-testid="meeting-feedback">
                                ✓ Reunião registrada. Follow-up de 30 dias agendado{lastFeedback.acoes > 0 ? ` e ${lastFeedback.acoes} ${lastFeedback.acoes === 1 ? "ação criada" : "ações criadas"} a partir dos passos numerados` : ""}.
                            </div>
                        )}
                    </form>
                </Card>
            </div>

            {/* Treinamentos */}
            <Card title={`Treinamentos (${treinos.length})`}
                subtitle="Sessões de capacitação realizadas ou agendadas com a equipe do cliente — as futuras aparecem na Agenda"
                testid="card-treinamentos">
                <div className="flex justify-end mb-3">
                    <button onClick={() => setShowTreino(!showTreino)} data-testid="toggle-treino-form" className="text-xs font-semibold border border-slate-300 hover:border-[#0F2044] text-[#0F2044] px-3 py-1.5 rounded-md inline-flex items-center gap-1">
                        <Plus className="h-3 w-3" /> {showTreino ? "Cancelar" : "Registrar treinamento"}
                    </button>
                </div>
                {showTreino && (
                    <form onSubmit={submitTreino} className="grid grid-cols-3 gap-3 bg-slate-50 border border-slate-100 rounded-md p-4 mb-4" data-testid="treino-form">
                        <Field label="Data"><input type="date" required value={tForm.data} onChange={(e) => setTForm({ ...tForm, data: e.target.value })} data-testid="treino-data" className="input" /></Field>
                        <Field label="Duração (min)"><input type="number" min={5} value={tForm.duracao_minutos} onChange={(e) => setTForm({ ...tForm, duracao_minutos: e.target.value })} data-testid="treino-duracao" className="input" /></Field>
                        <Field label="Qtd. participantes"><input type="number" min={0} value={tForm.quantidade_participantes} onChange={(e) => setTForm({ ...tForm, quantidade_participantes: e.target.value })} data-testid="treino-qtd" className="input" /></Field>
                        <div className="col-span-3">
                            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Módulo(s) treinado(s)</label>
                            <ModulosCheckboxes value={tForm.modulos} onChange={(m) => setTForm({ ...tForm, modulos: m })} testidPrefix="treino-modulo" />
                        </div>
                        <Field label="Participantes (nomes)"><input value={tForm.participantes} onChange={(e) => setTForm({ ...tForm, participantes: e.target.value })} data-testid="treino-part" className="input" /></Field>
                        <Field label="Entregue por"><input value={tForm.entregue_por} onChange={(e) => setTForm({ ...tForm, entregue_por: e.target.value })} data-testid="treino-cs" className="input" /></Field>
                        <Field label="Clima">
                            <select value={tForm.clima} onChange={(e) => setTForm({ ...tForm, clima: e.target.value })} data-testid="treino-clima" className="input">
                                {Object.entries(CLIMA_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                            </select>
                        </Field>
                        <div className="col-span-3"><Field label="Notas / resumo"><textarea rows={2} value={tForm.notas} onChange={(e) => setTForm({ ...tForm, notas: e.target.value })} data-testid="treino-notas" className="input" /></Field></div>
                        <div className="col-span-3"><Field label="Próximos passos"><textarea rows={2} value={tForm.proximos_passos} onChange={(e) => setTForm({ ...tForm, proximos_passos: e.target.value })} data-testid="treino-proximos" className="input" /></Field></div>
                        <div className="col-span-3"><Field label="Material compartilhado"><input value={tForm.material_compartilhado} onChange={(e) => setTForm({ ...tForm, material_compartilhado: e.target.value })} data-testid="treino-material" className="input" /></Field></div>
                        <div className="col-span-3 flex justify-end">
                            <button type="submit" disabled={tSaving} data-testid="treino-submit" className="bg-[#0F2044] hover:bg-[#172E5E] text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60">
                                {tSaving ? "Salvando…" : "Registrar treinamento"}
                            </button>
                        </div>
                    </form>
                )}
                {treinos.length === 0 ? (
                    <div className="text-sm text-slate-500 py-3 text-center">Nenhum treinamento registrado.</div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {treinos.map(t => (
                            <li key={t.id} className="py-3 flex items-start gap-3 group" data-testid={`treino-row-${t.id}`}>
                                <div className="text-sm font-mono-tab text-slate-500 w-24">{brl(t.data)}</div>
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-[#0F2044]">
                                        {(t.modulos && t.modulos.length ? t.modulos.join(", ") : t.modulo)} · {t.quantidade_participantes} part. · {t.duracao_minutos} min
                                        {t.clima && <ClimaBadge clima={t.clima} />}
                                        {t.data > new Date().toISOString().slice(0, 10) && (
                                            <span className="ml-2 badge-status" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid={`treino-agendado-${t.id}`}>Agendado</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        {t.entregue_por && <>Entregue por <strong>{t.entregue_por}</strong></>}
                                        {t.participantes && <span> · {t.participantes}</span>}
                                    </div>
                                    {t.notas && <div className="text-xs text-slate-600 mt-1">{t.notas}</div>}
                                    {t.observacoes && <div className="text-xs text-slate-600 mt-1">{t.observacoes}</div>}
                                    {t.proximos_passos && <div className="text-xs text-slate-600 mt-1"><strong>Próximos passos:</strong> {t.proximos_passos}</div>}
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => setEditingTreino(t)} data-testid={`treino-edit-${t.id}`}
                                        title="Editar treinamento" className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#0F2044]">
                                        <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                    <button onClick={() => deleteTreino(t.id)} data-testid={`treino-delete-${t.id}`}
                                        title="Excluir treinamento" className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600">
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            {editingMeeting && (
                <EditMeetingModal
                    contract={contract}
                    meeting={editingMeeting}
                    onClose={() => setEditingMeeting(null)}
                    onSaved={async () => { setEditingMeeting(null); await onUpdate(); }}
                />
            )}
            {editingTreino && (
                <EditTreinoModal
                    treino={editingTreino}
                    onClose={() => setEditingTreino(null)}
                    onSaved={async () => { setEditingTreino(null); await refreshT(); await onUpdate(); }}
                />
            )}
        </div>
    );
}

// M18.2 — edição de treinamento (sem duplicidade)
function EditTreinoModal({ treino, onClose, onSaved }) {
    const [form, setForm] = useState({
        data: treino.data || new Date().toISOString().slice(0, 10),
        modulos: treino.modulos && treino.modulos.length ? treino.modulos : (treino.modulo ? [treino.modulo] : []),
        duracao_minutos: treino.duracao_minutos ?? 60,
        quantidade_participantes: treino.quantidade_participantes ?? 0,
        participantes: treino.participantes || "",
        entregue_por: treino.entregue_por || "",
        clima: treino.clima || "bom",
        notas: treino.notas || "",
        proximos_passos: treino.proximos_passos || "",
        observacoes: treino.observacoes || "",
    });
    const [saving, setSaving] = useState(false);
    async function submit() {
        setSaving(true);
        try {
            await api.patch(`/treinamentos/${treino.id}`, {
                ...form,
                duracao_minutos: Number(form.duracao_minutos),
                quantidade_participantes: Number(form.quantidade_participantes),
            });
            await onSaved();
        } finally { setSaving(false); }
    }
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 overflow-y-auto py-6" data-testid="edit-treino-modal" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 fade-in my-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <h3 className="font-display font-semibold text-lg text-[#0F2044]">Editar treinamento</h3>
                    <button onClick={onClose} data-testid="edit-treino-close" className="text-slate-400 hover:text-slate-700 -mt-1"><X className="h-5 w-5" /></button>
                </div>
                <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Data"><input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="edit-treino-data" className="input" /></Field>
                        <Field label="Duração (min)"><input type="number" min={5} value={form.duracao_minutos} onChange={(e) => setForm({ ...form, duracao_minutos: e.target.value })} data-testid="edit-treino-duracao" className="input" /></Field>
                        <Field label="Qtd. particip."><input type="number" min={0} value={form.quantidade_participantes} onChange={(e) => setForm({ ...form, quantidade_participantes: e.target.value })} data-testid="edit-treino-qtd" className="input" /></Field>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Módulo(s) treinado(s)</label>
                        <ModulosCheckboxes value={form.modulos} onChange={(m) => setForm({ ...form, modulos: m })} testidPrefix="edit-treino-modulo" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Participantes"><input value={form.participantes} onChange={(e) => setForm({ ...form, participantes: e.target.value })} data-testid="edit-treino-part" className="input" /></Field>
                        <Field label="Clima">
                            <select value={form.clima} onChange={(e) => setForm({ ...form, clima: e.target.value })} data-testid="edit-treino-clima" className="input">
                                {Object.entries(CLIMA_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label="Notas / resumo"><textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="edit-treino-notas" className="input" /></Field>
                    <Field label="Próximos passos"><textarea rows={2} value={form.proximos_passos} onChange={(e) => setForm({ ...form, proximos_passos: e.target.value })} data-testid="edit-treino-proximos" className="input" /></Field>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="text-sm px-4 py-2 border border-slate-300 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={submit} disabled={saving} data-testid="edit-treino-save"
                        className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white rounded-md font-semibold disabled:opacity-60">
                        {saving ? "Salvando…" : "Salvar alterações"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// -------------- AÇÕES ------------------------------------------
function AcoesTab({ contract, actions, onUpdate }) {
    const [form, setForm] = useState({ descricao: "", responsavel: "", prazo: "", prioridade: "media" });
    const [saving, setSaving] = useState(false);
    const [editingAction, setEditingAction] = useState(null);  // M9 — editar ação

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/contracts/${contract.id}/actions`, form);
            await onUpdate();
            setForm({ descricao: "", responsavel: "", prazo: "", prioridade: "media" });
        } finally { setSaving(false); }
    }

    async function setStatus(a, status) {
        await api.patch(`/actions/${a.id}`, { status });
        await onUpdate();
    }

    const open = actions.filter(a => a.status !== "concluida");
    const done = actions.filter(a => a.status === "concluida");

    return (
        <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 space-y-6">
                <Card title={`Ações abertas (${open.length})`} testid="card-open-actions">
                    {open.length === 0 ? (
                        <div className="text-sm text-slate-500 py-3">Nenhuma ação aberta.</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {open.map(a => (
                                <li key={a.id} className="py-3" data-testid={`action-row-${a.id}`}>
                                    <div className="flex items-start gap-3">
                                        <PriorityDot prioridade={a.prioridade} />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-[#0F2044]">{a.descricao}</div>
                                            <div className="text-xs text-slate-500 mt-1">
                                                {a.responsavel
                                                    ? <span>{a.responsavel}</span>
                                                    : <span className="italic text-slate-400" data-testid={`action-sem-atribuicao-${a.id}`}>Sem atribuição</span>}
                                                {" "}· prazo {brl(a.prazo)} · {a.status}
                                            </div>
                                        </div>
                                        <button onClick={() => setEditingAction(a)} data-testid={`action-edit-${a.id}`}
                                            title="Editar ação"
                                            className="p-1.5 rounded text-slate-400 hover:text-[#0F2044] hover:bg-slate-100">
                                            <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                        {a.status !== "em_andamento" && (
                                            <button onClick={() => setStatus(a, "em_andamento")} data-testid={`action-start-${a.id}`} className="text-xs px-2 py-1 border border-slate-300 rounded-md hover:bg-slate-50">Iniciar</button>
                                        )}
                                        <button onClick={() => setStatus(a, "concluida")} data-testid={`action-done-${a.id}`} className="text-xs px-2 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md hover:bg-emerald-100">Concluir</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
                <Card title={`Concluídas (${done.length})`} testid="card-done-actions">
                    {done.length === 0 ? <div className="text-sm text-slate-500">Nenhuma ação concluída ainda.</div> : (
                        <ul className="divide-y divide-slate-100">
                            {done.map(a => (
                                <li key={a.id} className="py-2.5 flex items-start gap-2 text-sm" data-testid={`action-done-row-${a.id}`}>
                                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        {/* M3 — descrição completa, sem cortes */}
                                        <div className="text-slate-700 whitespace-pre-wrap break-words">{a.descricao}</div>
                                        <div className="text-xs text-slate-400 mt-0.5">
                                            {a.responsavel && <span>{a.responsavel} · </span>}Concluída em {brl(a.data_conclusao)}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>
            <Card title="Nova ação" testid="card-action-form">
                <form onSubmit={submit} className="space-y-3" data-testid="action-form">
                    <Field label="Descrição"><textarea rows={2} required value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} data-testid="action-desc-input" className="input" /></Field>
                    <Field label="Responsável (opcional)"><input type="text" value={form.responsavel} onChange={(e) => setForm({ ...form, responsavel: e.target.value })} placeholder="Sem atribuição" data-testid="action-resp-input" className="input" /></Field>
                    <Field label="Prazo"><input type="date" required value={form.prazo} onChange={(e) => setForm({ ...form, prazo: e.target.value })} data-testid="action-prazo-input" className="input" /></Field>
                    <Field label="Prioridade">
                        <select value={form.prioridade} onChange={(e) => setForm({ ...form, prioridade: e.target.value })} data-testid="action-priority-input" className="input">
                            <option value="alta">Alta</option><option value="media">Média</option><option value="baixa">Baixa</option>
                        </select>
                    </Field>
                    <button type="submit" disabled={saving} data-testid="action-submit-button" className="w-full bg-[#0F2044] hover:bg-[#172E5E] text-white text-sm font-semibold py-2 rounded-md disabled:opacity-60">
                        {saving ? "Salvando…" : "Criar ação"}
                    </button>
                </form>
            </Card>

            {editingAction && (
                <EditActionModal action={editingAction} onClose={() => setEditingAction(null)}
                    onSaved={async () => { setEditingAction(null); await onUpdate(); }} />
            )}
        </div>
    );
}

// M2/M9 — modal de edição de ação (descrição, responsável removível, prazo, prioridade)
function EditActionModal({ action, onClose, onSaved }) {
    const [descricao, setDescricao] = useState(action.descricao || "");
    const [responsavel, setResponsavel] = useState(action.responsavel || "");
    const [prazo, setPrazo] = useState(action.prazo || "");
    const [prioridade, setPrioridade] = useState(action.prioridade || "media");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    async function submit() {
        if (!descricao.trim() || !prazo) { setError("Preencha a descrição e o prazo."); return; }
        setSaving(true); setError("");
        try {
            await api.patch(`/actions/${action.id}`, {
                descricao: descricao.trim(), responsavel: responsavel.trim(), prazo, prioridade,
            });
            await onSaved();
        } catch (e) {
            setError(e?.response?.data?.detail || "Erro ao salvar a ação.");
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="edit-action-modal">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 fade-in">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#0F2044] font-semibold">Plano de ação</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">Editar ação</h3>
                    </div>
                    <button onClick={onClose} data-testid="edit-action-close" className="text-slate-400 hover:text-slate-700 -mt-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="space-y-4">
                    <Field label="Descrição *">
                        <textarea rows={3} value={descricao} onChange={(e) => setDescricao(e.target.value)} data-testid="edit-action-descricao" className="input" autoFocus />
                    </Field>
                    <Field label="Responsável">
                        <div className="flex items-center gap-2">
                            <input type="text" value={responsavel} onChange={(e) => setResponsavel(e.target.value)}
                                placeholder="Sem atribuição" data-testid="edit-action-responsavel" className="input flex-1" />
                            {responsavel && (
                                <button onClick={() => setResponsavel("")} data-testid="edit-action-remover-responsavel"
                                    title="Remover responsável" className="text-xs text-slate-500 hover:text-red-600 underline whitespace-nowrap">
                                    Remover
                                </button>
                            )}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">Deixe vazio para manter sem atribuição até alguém ser definido.</p>
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Prazo *">
                            <input type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} data-testid="edit-action-prazo" className="input" />
                        </Field>
                        <Field label="Prioridade">
                            <select value={prioridade} onChange={(e) => setPrioridade(e.target.value)} data-testid="edit-action-prioridade" className="input">
                                <option value="alta">Alta</option><option value="media">Média</option><option value="baixa">Baixa</option>
                            </select>
                        </Field>
                    </div>
                    {error && <div className="text-xs text-red-600 font-medium" data-testid="edit-action-error">{error}</div>}
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} data-testid="edit-action-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={submit} disabled={saving} data-testid="edit-action-save"
                        className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white rounded-md font-semibold disabled:opacity-60">
                        {saving ? "Salvando…" : "Salvar alterações"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// -------------- Helpers ------------------------------------------
function Card({ title, subtitle, children, className = "", testid, headerAction }) {
    return (
        <div className={`bg-white border border-slate-200 rounded-lg p-6 ${className}`} data-testid={testid}>
            {(title || subtitle || headerAction) && (
                <div className="mb-4 flex items-start justify-between gap-3">
                    <div>
                        {title && <h3 className="font-display font-semibold text-base text-[#0F2044]">{title}</h3>}
                        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
                    </div>
                    {headerAction && <div className="flex-shrink-0">{headerAction}</div>}
                </div>
            )}
            {children}
        </div>
    );
}
function DL({ children }) { return <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">{children}</dl>; }
function DT({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
            <div className="text-slate-800 mt-0.5">{children}</div>
        </div>
    );
}
function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">{label}</label>
            {children}
        </div>
    );
}
function TaskStatusIcon({ status, large }) {
    const size = large ? "h-5 w-5" : "h-4 w-4";
    if (status === "concluido") return <CheckCircle2 className={`${size} text-emerald-600`} />;
    if (status === "nao_se_aplica") return <MinusCircle className={`${size} text-slate-500`} />;
    if (status === "atrasado") return <AlertCircle className={`${size} text-red-600`} />;
    if (status === "em_andamento") return <Clock className={`${size} text-amber-600`} />;
    if (status === "bloqueado") return <Circle className={`${size} text-slate-300`} />;
    return <Circle className={`${size} text-slate-400`} />;
}
function ClimaBadge({ clima }) {
    const colors = {
        ruim: "bg-red-100 text-red-700",
        razoavel: "bg-amber-100 text-amber-800",
        bom: "bg-emerald-100 text-emerald-700",
        otimo: "bg-emerald-200 text-emerald-800",
    };
    return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${colors[clima]}`}>{CLIMA_LABELS[clima]}</span>;
}
function PriorityDot({ prioridade }) {
    const colors = { alta: "bg-red-500", media: "bg-amber-500", baixa: "bg-slate-400" };
    return <span className={`mt-1 h-2 w-2 rounded-full ${colors[prioridade] || colors.media}`} />;
}

// #5 — Editar reunião registrada
function EditMeetingModal({ contract, meeting, onClose, onSaved }) {
    const [form, setForm] = useState({
        data: meeting.data || "",
        tipo: meeting.tipo || "",
        participantes: meeting.participantes || "",
        clima: meeting.clima || "bom",
        notas: meeting.notas || "",
        proximos_passos: meeting.proximos_passos || "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    async function save() {
        setSaving(true); setError("");
        try {
            await api.patch(`/contracts/${contract.id}/meetings/${meeting.id}`, form);
            await onSaved();
        } catch (e) {
            setError(e?.response?.data?.detail?.toString?.() || "Falha ao salvar");
        } finally { setSaving(false); }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 overflow-y-auto py-8" data-testid="edit-meeting-modal">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 fade-in">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Editar reunião</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">{brl(meeting.data)} — {meeting.tipo}</h3>
                    </div>
                    <button onClick={onClose} data-testid="edit-meeting-close" className="text-slate-400 hover:text-slate-700">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-3">
                    <Field label="Data"><input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="edit-meeting-data" className="input" /></Field>
                    <Field label="Tipo"><input value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} data-testid="edit-meeting-tipo" className="input" /></Field>
                    <Field label="Participantes"><input value={form.participantes} onChange={(e) => setForm({ ...form, participantes: e.target.value })} data-testid="edit-meeting-participantes" className="input" /></Field>
                    <Field label="Clima">
                        <select value={form.clima} onChange={(e) => setForm({ ...form, clima: e.target.value })} data-testid="edit-meeting-clima" className="input">
                            {Object.entries(CLIMA_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                    </Field>
                    <Field label="Notas"><textarea rows={4} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="edit-meeting-notas" className="input" /></Field>
                    <Field label="Próximos passos">
                        <textarea rows={3} value={form.proximos_passos} onChange={(e) => setForm({ ...form, proximos_passos: e.target.value })} data-testid="edit-meeting-proximos" className="input" />
                        <p className="text-[10px] text-slate-500 mt-1">Editar aqui NÃO recria as ações. Ajuste o Plano de Ação diretamente para gerenciar os itens já criados.</p>
                    </Field>
                </div>

                {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mt-4">{error}</div>}

                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} data-testid="edit-meeting-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={save} disabled={saving} data-testid="edit-meeting-save" className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white font-semibold rounded-md inline-flex items-center gap-1 disabled:opacity-60">
                        {saving ? "Salvando…" : "Salvar alterações"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// #10 — Editar dados cadastrais (plano fica fixo)
function EditContractModal({ contract, onClose, onSaved }) {
    const [form, setForm] = useState({
        organizacao_nome: contract.organizacao_nome || "",
        esfera: contract.esfera || "municipal",
        data_assinatura: contract.data_assinatura || "",
        data_vencimento: contract.data_vencimento || "",
        data_entrega_produto: contract.data_entrega_produto || "",
        vendedor_responsavel: contract.vendedor_responsavel || contract.cs_responsavel || "",
        ponto_focal: {
            nome: contract.ponto_focal?.nome || "",
            cargo: contract.ponto_focal?.cargo || "",
            email: contract.ponto_focal?.email || "",
            telefone: contract.ponto_focal?.telefone || "",
            fiscal_nome: contract.ponto_focal?.fiscal_nome || "",
            fiscal_email: contract.ponto_focal?.fiscal_email || "",
            fiscal_telefone: contract.ponto_focal?.fiscal_telefone || "",
        },
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    function setPF(k, v) { setForm(prev => ({ ...prev, ponto_focal: { ...prev.ponto_focal, [k]: v } })); }

    async function save() {
        setSaving(true); setError("");
        try {
            const payload = { ...form };
            if (!payload.data_entrega_produto) payload.data_entrega_produto = null;
            await api.patch(`/contracts/${contract.id}`, payload);
            await onSaved();
        } catch (e) {
            setError(e?.response?.data?.detail?.toString?.() || "Falha ao salvar");
        } finally { setSaving(false); }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 overflow-y-auto py-8" data-testid="edit-contract-modal">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 fade-in">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Editar dados cadastrais</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">{contract.organizacao_nome}</h3>
                    </div>
                    <button onClick={onClose} data-testid="edit-contract-close" className="text-slate-400 hover:text-slate-700">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Dados do cliente</div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Organização">
                        <input value={form.organizacao_nome} onChange={(e) => setForm({ ...form, organizacao_nome: e.target.value })} data-testid="edit-org" className="input" />
                    </Field>
                    <Field label="Esfera">
                        <select value={form.esfera} onChange={(e) => setForm({ ...form, esfera: e.target.value })} data-testid="edit-esfera" className="input">
                            <option value="federal">Federal</option>
                            <option value="estadual">Estadual</option>
                            <option value="municipal">Municipal</option>
                        </select>
                    </Field>
                    <Field label="Plano (não editável)">
                        <input value={PLANO_LABELS[contract.plano]} disabled data-testid="edit-plano-readonly" className="input bg-slate-100 text-slate-500 cursor-not-allowed" />
                    </Field>
                    <Field label="Vendedor responsável">
                        <input value={form.vendedor_responsavel} onChange={(e) => setForm({ ...form, vendedor_responsavel: e.target.value })} data-testid="edit-vendedor" className="input" />
                    </Field>
                    <Field label="Data de assinatura">
                        <input type="date" value={form.data_assinatura} onChange={(e) => setForm({ ...form, data_assinatura: e.target.value })} data-testid="edit-data-assinatura" className="input" />
                    </Field>
                    <Field label="Data de vencimento">
                        <input type="date" value={form.data_vencimento} onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })} data-testid="edit-data-vencimento" className="input" />
                    </Field>
                    <Field label="Data de entrega do produto">
                        <input type="date" value={form.data_entrega_produto || ""} onChange={(e) => setForm({ ...form, data_entrega_produto: e.target.value })} data-testid="edit-data-entrega" className="input" />
                    </Field>
                </div>

                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mt-5 mb-2">Ponto focal</div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Nome"><input value={form.ponto_focal.nome} onChange={(e) => setPF("nome", e.target.value)} data-testid="edit-pf-nome" className="input" /></Field>
                    <Field label="Cargo"><input value={form.ponto_focal.cargo} onChange={(e) => setPF("cargo", e.target.value)} data-testid="edit-pf-cargo" className="input" /></Field>
                    <Field label="E-mail"><input type="email" value={form.ponto_focal.email} onChange={(e) => setPF("email", e.target.value)} data-testid="edit-pf-email" className="input" /></Field>
                    <Field label="Telefone"><input value={form.ponto_focal.telefone} onChange={(e) => setPF("telefone", e.target.value)} data-testid="edit-pf-tel" className="input" /></Field>
                </div>

                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mt-5 mb-2">Fiscal do contrato</div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Nome"><input value={form.ponto_focal.fiscal_nome} onChange={(e) => setPF("fiscal_nome", e.target.value)} data-testid="edit-fiscal-nome" className="input" /></Field>
                    <div />
                    <Field label="E-mail"><input type="email" value={form.ponto_focal.fiscal_email} onChange={(e) => setPF("fiscal_email", e.target.value)} data-testid="edit-fiscal-email" className="input" /></Field>
                    <Field label="Telefone"><input value={form.ponto_focal.fiscal_telefone} onChange={(e) => setPF("fiscal_telefone", e.target.value)} data-testid="edit-fiscal-tel" className="input" /></Field>
                </div>

                {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mt-4">{error}</div>}

                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} data-testid="edit-contract-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={save} disabled={saving} data-testid="edit-contract-save" className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white font-semibold rounded-md inline-flex items-center gap-1 disabled:opacity-60">
                        {saving ? "Salvando…" : "Salvar alterações"}
                    </button>
                </div>
            </div>
        </div>
    );
}
